/**
 * zstd wrap/unwrap for chunk upload (`x-pkil-encoding: zstd`) and SRAM blobs.
 *
 * Library: @bokuweb/zstd-wasm, the reference libzstd compiled to one WASM binary. Its package exports a
 * Node entry (reads zstd.wasm from disk) and a browser entry (fetches zstd.wasm via import.meta.url), so
 * client and server run the same compressor and produce the same bytes at the same level. Hashes are
 * always over the *uncompressed* bytes, so compression never affects the chain.
 *
 * Browser bundling (Vite): exclude the package from dependency pre-bundling
 * (`optimizeDeps.exclude: ["@bokuweb/zstd-wasm"]`) so its `new URL("./zstd.wasm", import.meta.url)`
 * resolves, or call configureZstd({ wasmUrl }) with a URL you serve yourself before first use.
 */
import { PkilError } from "./errors.js";

interface ZstdModule {
  init(path?: string): Promise<void>;
  compress(buf: Uint8Array, level?: number): Uint8Array;
  decompress(buf: Uint8Array, opts?: { defaultHeapSize?: number }): Uint8Array;
}

/** Compression level used for every PKIL chunk and SRAM upload. */
export const ZSTD_LEVEL = 3;
/** Default cap on decompressed size; protects servers from decompression bombs. */
export const DEFAULT_MAX_DECOMPRESSED_BYTES = 64 * 1024 * 1024;

const ZSTD_MAGIC = 0xfd2fb528;

let wasmUrl: string | undefined;
let ready: Promise<ZstdModule> | null = null;

/** Overrides where the browser build fetches zstd.wasm. Must be called before the first zstd call. */
export function configureZstd(opts: { wasmUrl?: string }): void {
  if (ready) throw new PkilError("ZSTD", "configureZstd must be called before zstd is first used");
  wasmUrl = opts.wasmUrl;
}

function pickModule(ns: unknown): ZstdModule {
  const isMod = (m: unknown): m is ZstdModule =>
    typeof m === "object" && m !== null && typeof (m as ZstdModule).compress === "function" && typeof (m as ZstdModule).init === "function";
  if (isMod(ns)) return ns;
  const def = (ns as { default?: unknown }).default;
  if (isMod(def)) return def;
  throw new PkilError("ZSTD", "@bokuweb/zstd-wasm did not expose init/compress/decompress");
}

/** Loads and initialises the WASM module once; safe to await repeatedly. */
export function loadZstd(): Promise<void> {
  return getZstd().then(() => undefined);
}

function getZstd(): Promise<ZstdModule> {
  ready ??= (async () => {
    const mod = pickModule(await import("@bokuweb/zstd-wasm"));
    await (wasmUrl === undefined ? mod.init() : mod.init(wasmUrl));
    return mod;
  })().catch((e: unknown) => {
    ready = null;
    throw e instanceof PkilError ? e : new PkilError("ZSTD", `failed to initialise zstd WASM: ${String(e)}`);
  });
  return ready;
}

export async function zstdCompress(b: Uint8Array): Promise<Uint8Array> {
  const z = await getZstd();
  try {
    return z.compress(b, ZSTD_LEVEL);
  } catch (e) {
    throw new PkilError("ZSTD", `compression failed: ${String(e)}`);
  }
}

/**
 * Reads Frame_Content_Size from the first zstd frame header (RFC 8878 §3.1.1.1). Returns null when the
 * frame does not record it. Throws if `b` does not start with a zstd frame.
 */
export function zstdFrameContentSize(b: Uint8Array): bigint | null {
  if (b.length < 5) throw new PkilError("ZSTD", "input too short for a zstd frame");
  const view = new DataView(b.buffer, b.byteOffset, b.byteLength);
  if (view.getUint32(0, true) !== ZSTD_MAGIC) throw new PkilError("ZSTD", "input is not a zstd frame");
  const fhd = b[4]!;
  const fcsFlag = fhd >> 6;
  const singleSegment = (fhd >> 5) & 1;
  const dictIdSize = [0, 1, 2, 4][fhd & 3]!;
  const fcsSize = fcsFlag === 0 ? singleSegment : [0, 2, 4, 8][fcsFlag]!;
  if (fcsSize === 0) return null;
  const at = 5 + (singleSegment ? 0 : 1) + dictIdSize;
  if (b.length < at + fcsSize) throw new PkilError("ZSTD", "truncated zstd frame header");
  switch (fcsSize) {
    case 1:
      return BigInt(b[at]!);
    case 2:
      return BigInt(view.getUint16(at, true) + 256);
    case 4:
      return BigInt(view.getUint32(at, true));
    default:
      return view.getBigUint64(at, true);
  }
}

export async function zstdDecompress(b: Uint8Array, opts: { maxBytes?: number } = {}): Promise<Uint8Array> {
  const max = opts.maxBytes ?? DEFAULT_MAX_DECOMPRESSED_BYTES;
  const declared = zstdFrameContentSize(b);
  if (declared !== null && declared > BigInt(max)) throw new PkilError("TOO_LARGE", `zstd frame declares ${declared} bytes, limit ${max}`);
  const z = await getZstd();
  try {
    // Without a declared size the library decompresses into a buffer of defaultHeapSize and fails if it overflows.
    return z.decompress(b, { defaultHeapSize: max });
  } catch (e) {
    throw new PkilError("ZSTD", `decompression failed: ${String(e)}`);
  }
}
